import { Component, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';
import { fadeAnimation } from 'src/animations';
import { PaginationResponse } from 'src/app/classes';
import { Location } from '../../../classes';
import { LocationPipe } from '../../../pipes/location.pipe';
import { LocationService } from '../../../services/location.service';

@Component({
  selector: 'app-select-location2',
  templateUrl: './select-location2.component.html',
  styleUrls: ['./select-location2.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectLocation2Component),
      multi: true
    },
    LocationPipe
  ],
  animations: [fadeAnimation]
})
export class SelectLocation2Component implements ControlValueAccessor, OnDestroy, OnInit {

  currentLabel: string = "Standort";
  @Input() set label(value: string) {
    this.currentLabel = value ? value : 'Standort';
  }
  @Input() appearance: 'standard' | 'fill' | 'outline' = 'standard';

  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
  @ViewChild("inputField", { static: true }) inputField: ElementRef<HTMLInputElement>;

  @Output() selectionChanged: EventEmitter<number> = new EventEmitter<number>();

  filterControl: FormControl = new FormControl();
  valueControl: FormControl = new FormControl();

  locations: Location[] = [];
  currentLocation: Location = null;

  private _loading = new BehaviorSubject(false);
  private _subscriptions: Subscription[] = [];

  constructor(
    private _locationPipe: LocationPipe,
    private _locationService: LocationService
  ) { }

  display = (location: Location): Observable<string> => this._locationPipe.transform(location.locationid);

  ngOnDestroy(): void {
    for (let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
  }

  ngOnInit(): void {
    this._subscriptions.push(...[
      this._locationService.locations$.pipe(take(1), takeUntil(this._loading.pipe(filter(x => x)))).subscribe(locations => {
        this.locations = (locations as any[]);
      }),
      this.filterControl.valueChanges
        .pipe(
          debounceTime(1000),
          distinctUntilChanged()
        )
        .subscribe((value) => this.search(value))
    ]);
  }

  onTouched: () => void = () => { };
  registerOnChange = (fn: any) => this._subscriptions.push(this.valueControl.valueChanges.subscribe(fn));
  registerOnTouched = (fn: any) => this.onTouched = fn;

  search(term: string) {
    if (typeof term != 'string') term = '';
    this._loading.next(true);
    let normalized = term.trim().toLocaleLowerCase();
    this._locationService.searchLocationsByString(normalized, { 'pageNumber': 1, 'pageSize': 20 })
      .subscribe({
        next: (response: PaginationResponse<Location[]>) => {
          this.locations = response.content;
          this._loading.next(false);
        }
      });
  }

  selectLocation(event: MatAutocompleteSelectedEvent) {
    if (!event || !event.option || !event.option.value) {
      this.currentLocation = null;
      this.label = "Standort";
      this.filterControl.setValue('');
      this.selectionChanged.emit(null);
      if (this.valueControl.value) this.valueControl.setValue(null);
    } else if (this.currentLocation != event.option.value) {
      this.currentLocation = event.option.value;
      if (this.currentLocation) this.display(this.currentLocation).subscribe((result) => this.label = result);
      this.filterControl.setValue('');
      this.selectionChanged.emit(event.option.value ? (event.option.value as Location).locationid : null);
      if (this.valueControl.value !== event.option.value.locationid) this.valueControl.setValue(event.option.value ? event.option.value.locationid : null);
    }
    this.inputField.nativeElement.blur();
    if (this.autocomplete && this.autocomplete.panelOpen) this.autocomplete.closePanel();
  }

  setDisabledState?= (isDisabled: boolean) => {
    isDisabled ? this.valueControl.disable() : this.valueControl.enable();
    isDisabled ? this.filterControl.disable() : this.filterControl.enable();
  }

  setLocationIfOneLeft() {
    if (this.locations.length === 1) this.currentLocation = this.locations[0];
  }

  writeValue(val: any): void {
    this.valueControl.patchValue(val);
    if (typeof val === 'number') this._locationService.getLocationById(val, true).pipe(take(1)).subscribe((location: Location) => this.selectLocation({ option: { value: location } } as any));
  }

}
